/**
 * PLAT-9968 — pure resolution of the Creatomate `activeElementIds[0]`
 * value into a `CanvasTarget` the action menu / quick-edit popover can
 * render against.
 *
 * Three resolution buckets, in priority order:
 *
 *   1. **Composition placement** — the active id is the placement itself
 *      OR any descendant id inside a top-level `type: 'composition'`
 *      element. Used for intro / end / nameCard surfaces where the
 *      composition's `extraElementData[compositionId]` carries the type
 *      + template label.
 *
 *   2. **Text-bearing node** — any node in the tree with the active id
 *      and a non-empty `text` property. Covers standalone overlays at
 *      the top level AND nested text leaves inside group / composition
 *      elements when the iframe surfaces the inner id directly (Path B
 *      compositions).
 *
 *   3. **Last-resort selection** — element exists with the active id
 *      somewhere but isn't editable. Surfaced so the action menu's
 *      Delete works and a missing menu always means selection wiring is
 *      broken, never our resolution.
 *
 * Extracted from `CompositionCanvasActionsLayer` so the resolution
 * chain stays unit-testable without React / MobX / adapter mocking.
 */
import {
  findContainingCompositionId,
  findNodeInTree,
  hasNonEmptyTextProperty,
} from './compositionElementPath';

import { formatHeaderLabelFromMarker } from './compositionLabels';

export type CanvasTargetKind = 'composition' | 'overlay';

export type CanvasTarget = {
  /** Top-level element id we're editing (composition placement OR overlay). */
  id: string;
  kind: CanvasTargetKind;
  /** Specific composition discriminator when known; used for analytics. */
  compositionType: 'introCard' | 'endCard' | 'nameCard' | 'overlay';
  /** Header text shown on the popover. */
  headerLabel: string;
  /** Accessible name for the floating toolbar. */
  ariaLabel: string;
};

interface ResolveArgs {
  activeId: string | undefined;
  videoSourceElements:
    | ReadonlyArray<Record<string, unknown>>
    | undefined
    | null;
  extraElementData: Record<string, unknown> | null;
}

export function resolveCanvasTarget(
  args: ResolveArgs,
): CanvasTarget | null {
  const { activeId, videoSourceElements, extraElementData } = args;
  if (!activeId) return null;

  if (!Array.isArray(videoSourceElements)) {
    return {
      id: activeId,
      kind: 'overlay',
      compositionType: 'overlay',
      headerLabel: 'Selection',
      ariaLabel: 'Selection actions',
    };
  }

  const topLevel = videoSourceElements;

  // 1. Composition placement (active id may be the placement or any
  //    nested descendant).
  const compositionId = findContainingCompositionId(topLevel, activeId);
  if (compositionId) {
    const meta =
      extraElementData && typeof extraElementData === 'object'
        ? (extraElementData as Record<string, unknown>)[compositionId]
        : null;
    const metaRecord =
      meta && typeof meta === 'object'
        ? (meta as Record<string, unknown>)
        : null;
    const compositionType = metaRecord?.compositionType;
    const templateLabel = metaRecord?.templateLabel;
    const normalizedType =
      compositionType === 'introCard' ||
      compositionType === 'endCard' ||
      compositionType === 'nameCard'
        ? compositionType
        : undefined;
    const labelStr =
      typeof templateLabel === 'string' ? templateLabel : undefined;
    const headerLabel = formatHeaderLabelFromMarker({
      type: normalizedType ?? 'overlay',
      label: labelStr,
    });
    return {
      id: compositionId,
      kind: 'composition',
      compositionType: normalizedType ?? 'overlay',
      headerLabel,
      ariaLabel: `${headerLabel} actions`,
    };
  }

  // 2. Any text-bearing element anywhere in the tree.
  const textNode = findNodeInTree(topLevel, activeId, hasNonEmptyTextProperty);
  if (textNode) {
    const overlayName = (textNode as { name?: unknown }).name;
    const headerLabel =
      typeof overlayName === 'string' && overlayName !== ''
        ? overlayName
        : 'Text overlay';
    return {
      id: activeId,
      kind: 'overlay',
      compositionType: 'overlay',
      headerLabel,
      ariaLabel: `${headerLabel} actions`,
    };
  }

  // 3. Last-resort: an element with this id exists somewhere but
  //    isn't editable, OR no element matches at all. Surface the menu
  //    anyway so Delete works and a missing menu always means the
  //    selection wiring is broken, never our resolution.
  return {
    id: activeId,
    kind: 'overlay',
    compositionType: 'overlay',
    headerLabel: 'Selection',
    ariaLabel: 'Selection actions',
  };
}
